import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuTrash2 } from 'react-icons/lu';

import { Button } from '~/components/ui/button';
import { useGlobalPopup } from '~/components/design/popup/GlobalPopup/useGlobalPopup';
import { HighscoresAPI } from '~/services/db/highscores/HighscoresAPI';

import styles from './DeleteScoresButton.module.css';

export type DeleteScoresButtonProps = {
    clientID: string;
    gameID: string;
};

function ConfirmDeleteContent({
    clientID,
    gameID,
    onClose,
}: {
    clientID: string;
    gameID: string;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        setIsDeleting(true);
        try {
            await HighscoresAPI.deleteAllScores(clientID, gameID);
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={styles.confirmContainer}>
            <p className={styles.confirmTitle}>{t('console.deleteScores.confirmTitle')}</p>
            <p className={styles.confirmMessage}>{t('console.deleteScores.confirmMessage')}</p>
            <div className={styles.confirmActions}>
                <Button preset="secondary" onClick={onClose} disabled={isDeleting}>
                    {t('console.deleteScores.cancel')}
                </Button>
                <Button className={styles.dangerButton} onClick={handleConfirm} disabled={isDeleting}>
                    {isDeleting ? t('console.deleteScores.deleting') : t('console.deleteScores.confirm')}
                </Button>
            </div>
        </div>
    );
}

export function DeleteScoresButton({ clientID, gameID }: DeleteScoresButtonProps) {
    const { t } = useTranslation();
    const { open, close } = useGlobalPopup();

    const handleClick = () => {
        open(<ConfirmDeleteContent clientID={clientID} gameID={gameID} onClose={close} />);
    };

    return (
        <Button className={styles.deleteButton} preset="secondary" icon={<LuTrash2 />} onClick={handleClick}>
            {t('console.deleteScores.button')}
        </Button>
    );
}
